import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  getMobileValidationError,
} from "../utils/mobileInput";
import {
  emailInputFieldProps,
  getEmailValidationError,
} from "../utils/emailInput";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import BranchAccessEditor, {
  normalizeBranchAccessesFromUser,
  toBranchAccessesPayload,
} from "../components/permissions/BranchAccessEditor";
import { getUserData, setUserData } from "./const";
import {
  getAdminUser,
  updateAdminUser,
  erpFetch,
  isErpApiSuccess,
  fetchErpMe,
  getErpAuthState,
} from "../erpClient";

function EditEmployee() {
  const { buttonClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    status: 1,
  });
  const [branchAccesses, setBranchAccesses] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("userDetails");
  const userData = getUserData();

  useEffect(() => {
    const handleApiUrlChange = () => {
      toast("Database switched. Returning to employees table.");
      navigate("/employee");
    };
    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => window.removeEventListener("apiUrlChanged", handleApiUrlChange);
  }, [navigate]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userId = location.state?.userId;
        if (!userId) {
          toast.error("No user selected");
          navigate("/employee");
          return;
        }

        const [userRes, branchRes] = await Promise.all([
          getAdminUser({ id: Number(userId) }),
          erpFetch("/branch", { method: "POST", body: { type: "list" } }),
        ]);

        if (!(userRes?.success || isErpApiSuccess(userRes)) || !userRes.data) {
          toast.error(userRes?.message || "Failed to fetch user details");
          navigate("/employee");
          return;
        }

        const userObj = userRes.data.user || userRes.data;
        const sessionUser = getErpAuthState().user || {};
        const tenantId = Number(
          sessionUser.tenant_id ??
            sessionUser.tenantId ??
            userObj?.tenant_id ??
            userObj?.tenantId ??
            userObj?.tenant?.id,
        );

        if (isErpApiSuccess(branchRes) && Array.isArray(branchRes?.data?.branches)) {
          const scoped =
            Number.isFinite(tenantId) && tenantId > 0
              ? branchRes.data.branches.filter(
                  (b) =>
                    Number(b.tenant_id ?? b.tenant?.id) === tenantId,
                )
              : branchRes.data.branches;
          setBranchOptions(
            scoped.map((b) => ({
              value: b.id,
              id: b.id,
              label: b.code ? `${b.name} (${b.code})` : b.name,
              name: b.name,
            })),
          );
        }
        setFormData({
          name: userObj?.name || "",
          phone: userObj?.phone || "",
          email: userObj?.email || "",
          status: parseInt(userObj?.status, 10) || 1,
        });
        setBranchAccesses(normalizeBranchAccessesFromUser(userObj));
      } catch (error) {
        console.error("Error fetching user details:", error);
        toast.error(
          error.response?.data?.message || "Failed to fetch user details",
        );
        navigate("/employee");
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [location.state?.userId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!location.state?.userId) {
      toast.error("No user ID found");
      return;
    }

    const mobileError = getMobileValidationError(formData.phone, {
      required: true,
    });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(formData.email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    const payloadAccesses = toBranchAccessesPayload(branchAccesses);
    if (!payloadAccesses.length) {
      toast.error("Add at least one branch access");
      return;
    }
    if (!payloadAccesses.some((r) => r.is_default)) {
      toast.error("Mark one branch as default");
      return;
    }
    for (const row of payloadAccesses) {
      if (!row.role_ids?.length) {
        toast.error("Each branch access needs a role");
        return;
      }
    }

    setSaving(true);
    try {
      const statusToSend =
        Number.isFinite(parseInt(formData.status, 10)) &&
        String(formData.status) !== ""
          ? parseInt(formData.status, 10)
          : 1;

      const defaultRow = payloadAccesses.find((r) => r.is_default);

      const data = await updateAdminUser({
        id: Number(location.state?.userId),
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        status: statusToSend,
        branch_accesses: payloadAccesses,
        // Compatibility: also send default branch flat fields
        branch_id: defaultRow?.branch_id,
        role_ids: defaultRow?.role_ids,
        permission_codes: defaultRow?.permission_codes,
      });

      if (isErpApiSuccess(data) || data?.message === "radha") {
        toast.success("Employee updated successfully");

        const currentUserId = userData?.id || userData?.ua_id;
        const updatedUserId = location.state?.userId;

        if (currentUserId && updatedUserId && currentUserId == updatedUserId) {
          try {
            await fetchErpMe();
          } catch {
            // ignore
          }
          setUserData({
            ...userData,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            status: formData.status,
          });
          setTimeout(() => {
            navigate("/employee", {
              state: { userId: location.state?.userId },
              replace: true,
            });
          }, 500);
        } else {
          navigate("/employee");
        }
      } else {
        toast.error(data?.message || "Failed to update employee");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Failed to update employee");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="body-wrapper">
        <div className="container-fluid">
          <div className={`card ${cardClass}`}>
            <div className="card-body text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading user details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className={`card ${cardClass}`}>
          <div className="card-body">
            <form onSubmit={handleSubmit} id="editEmployeeForm">
              <div className="d-flex justify-content-center mb-3">
                <div
                  className={`d-inline-flex overflow-hidden border bg-white shadow-sm ${
                    buttonTableStyle === "square" ? "rounded-0" : "rounded-pill"
                  }`}
                >
                  {[
                    ["userDetails", "User Details"],
                    ["branchAccess", "Branch Access"],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`btn border-0 rounded-0 px-4 py-2 ${
                        activeTab === key
                          ? "btn-primary text-white"
                          : "bg-white text-dark"
                      } ${buttonClass}`}
                      style={{ minWidth: "140px", boxShadow: "none" }}
                      onClick={() => setActiveTab(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab === "userDetails" ? (
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Enter Full Name"
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number</Form.Label>
                      <Form.Control
                        {...mobileInputFieldProps}
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            phone: sanitizeMobileInput(e.target.value),
                          }))
                        }
                        placeholder="Enter Mobile Number"
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        {...emailInputFieldProps}
                        value={formData.email}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="Enter Email Address"
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <div className="d-flex gap-3">
                        <Form.Check
                          type="radio"
                          label="Active"
                          name="status"
                          value="1"
                          checked={formData.status === 1}
                          onChange={() =>
                            setFormData((prev) => ({ ...prev, status: 1 }))
                          }
                        />
                        <Form.Check
                          type="radio"
                          label="Inactive"
                          name="status"
                          value="2"
                          checked={formData.status === 2}
                          onChange={() =>
                            setFormData((prev) => ({ ...prev, status: 2 }))
                          }
                        />
                      </div>
                    </Form.Group>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-muted small mb-3">
                    Assign one or more branches. Pick a role per branch →
                    permissions auto-select → customize ticks for that branch
                    only. Mark exactly one default branch.
                  </p>
                  <BranchAccessEditor
                    value={branchAccesses}
                    onChange={setBranchAccesses}
                    branchOptions={branchOptions}
                    square={buttonTableStyle === "square"}
                  />
                </div>
              )}

              <div className="row mt-4">
                <div className="col-12">
                  <div className="d-flex justify-content-end">
                    <button
                      type="submit"
                      className={`btn btn-success ${buttonClass}`}
                      disabled={saving}
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditEmployee;
